import { FundBenchmarking } from '@/modules/analysis/benchmarking/fund-benchmarking';
import { CurrencyPreference } from '@/modules/reporting/types';
import { CustomLocale } from '@/utils/Utils';

export interface PaginatedRequestParams {
  page?: number | string;
  limit?: number | string;
}
export type PaginatedParams<R> = R & PaginatedRequestParams;

export type FundAnalysisParams = PaginatedParams<{
  filters?: FundAnalysisFilters;
  useStubData?: boolean;
}>;

// Fund Analysis Response Type
export interface PaginationMeta {
  current_page?: number;
  from?: number;
  last_page?: number;
  path?: string;
  per_page?: number;
  to?: number;
  total?: number;
  links?: any;
}

export interface PaginationLinks {
  first?: string;
  last?: string;
  prev?: string;
  next?: string;
}

export interface Paginated<T> {
  data: T[];
  meta: PaginationMeta;
  links?: PaginationLinks;
}

export interface PaginatedV2<T> {
  items: T[];
  meta: PaginationMeta;
  links?: PaginationLinks;
}
//

export enum BenchmarkingStatusSlug {
  GOOD_FOR_BENCHMARKING = 'good_for_benchmarking',
  MCLAGAN_TO_REVIEW = 'mclagan_to_review',
  CLEARGLASS_TO_REVIEW = 'clearglass_to_review',
  DATA_OK_BUT_NOT_BENCHMARKABLE = 'data_ok_but_not_benchmarkable',
  BENCHMARKING_OUTLIER = 'benchmarking_outlier',
  NO_BENCHMARKING_TAG = 'no_benchmarking_tag'
}

export type Benchmarking =
  | BenchmarkingStatusSlug.GOOD_FOR_BENCHMARKING
  | BenchmarkingStatusSlug.MCLAGAN_TO_REVIEW
  | BenchmarkingStatusSlug.CLEARGLASS_TO_REVIEW
  | BenchmarkingStatusSlug.DATA_OK_BUT_NOT_BENCHMARKABLE
  | BenchmarkingStatusSlug.BENCHMARKING_OUTLIER;

export type FundAnalysisFilters = {
  clientId: string;
  organizationIds?: string[];
  parentId?: string[];
  parentInvestmentId?: string[];
  pensionSchemeIds?: string[];
  psEntityIds?: string[];
  subFundIds?: string[];
  entityIds?: string[];
  sfEntityIds?: string[];
  fundIds?: string[];
  assetClassIds?: string[];
  fundTypeIds?: string[];
  assetManagerIds?: string[];
  reportingPeriodIds?: string[];
  templateIds?: string[];
  responseFilter?: string[];
  status?: SubmissionStatusMapping;
  searchKeyword?: string;
  requestStartDate?: string;
  requestEndDate?: string;
  page?: number;
  limit?: number;
  excludeFundsWithNoAum?: boolean;
  enableSubIdPicking?: boolean;
  includeUnderFund?: boolean;
  overrideAumBasedRelativeValue?: boolean;
  columnOrder?: string[];
  includeComments?: boolean;
  annualizationTags?: string[];
  investmentPeriodTags?: string[];
  includeOnlyExposedData?: boolean;
  includeBenchmarking?: boolean;
};

export enum SubmissionStatusMapping {
  EXPOSED = 'EXPOSED',
  ERROR = 'ERROR',
  COMPLETED = 'COMPLETED',
  DRAFT = 'DRAFT',
  FAILED = 'FAILED',
  REJECTED = 'REJECTED',
  EXPIRED = 'EXPIRED',
  PENDING = 'PENDING',
  ADDED = 'ADDED',
  'FAILED BY DATA TEAM' = 'FAILED BY DATA TEAM',
  request_acknowledged = 'request_acknowledged',
  SUBMITTED = 'SUBMITTED',
  past_due_date = 'past_due_date'
}

export interface FundAnalysis
  extends ExtractedFundContext,
    CostData,
    DateFields,
    AssetValuesV1 {
  reportingCurrency?: string;
  conversionRate?: number;
  conversionRateDate?: string;
  sheetNumber?: number;
  rowColor?: any;
  font?: any;
  isRollupRequired?: boolean;
  aumTag?: string;
  providedAssetBaseValue?: number;
  denominatedAum?: number;
  preferredAssetBase?: string;
  preferredAssetBaseSlug?: string;
  net_return?: number;
  net_return_percent?: number;
  gross_return?: number;
  gross_return_percent?: number;
  netReturnCalculated?: boolean;
  assets_under_management?: number;
  factored_assets_under_management?: number;
  provided_assets_under_management?: number;

  providedCommitmentValue?: number;
  portfolioIssuerName?: string;
  portfolioShareClassName?: string;
  clientName?: string;
  portfolioIdentifier?: string;
  comments?: Comments[];
  excelParentInvestmentId?: string;
  isUnderFund?: boolean;
  benchmarking?: FundBenchmarking;
}

export interface ExtractedFundContext {
  amName?: string;
  amParentOrgName?: string;
  amParentOrganization?: string;
  amAbbreviatedName?: string;
  amFormerName?: string;
  amManagerType?: string;
  isLatestInvestment?: boolean;
  amStatus?: string;
  fundName?: string;
  parentName?: string;
  rootOrganisationName?: string;
  subFundName?: string;
  schemeName?: string;
  root_id?: string;
  manager_organisation_id?: string;
  investmentId?: string;
  entityId?: string;
  parentId?: string;
  parentInvestmentId?: string;
  templateTypeSlug?: string;
  status?: string;
  benchmarkingStatus?: string;
  benchmarkingSalesStatus?: string;
  fundType?: string;
  fundTypeSlug?: string;
  fundNature?: string;
  fundNatureSlug?: string;
  productType?: string;
  completeStrategy?: string;
  productTypeSlug?: string;
  thirdLevelStrategy?: string;
  thirdLevelStrategySlug?: string;
  timePeriodRange?: string;
  submissionId?: string;
  submissionCreatedAt?: string;
  submissionUpdatedAt?: string;
  submissionUploadedBy?: string;
  uniqueIdentifier?: string;
  uniqueIdentifierType?: string;
  internalCode?: string;
  internalPortfolioCode?: string;
  consultantName?: string;
  internalClientCode?: string;
  children?: FundAnalysis[];
}

export interface CostData {
  administration?: number;
  administration_percent?: number;
  ancillary_costs?: number;
  annualised_administration_percent?: number;
  annualised_borrowing_and_arrangement_fees_percent?: number;
  annualised_broker_commissions_exchange_fees_percent?: number;
  annualised_carried_interest_charged_percent?: number;
  annualised_client_fx_costs_percent?: number;
  annualised_direct_ongoing_charges_percent?: number;
  annualised_distribution_comms_and_client_service_percent?: number;
  annualised_entry_costs_percent?: number;
  annualised_exit_costs_percent?: number;
  annualised_fees_and_charges_paid_through_nav_percent?: number;
  annualised_fund_and_investment_management_percent?: number;
  annualised_governance_regulation_and_compliance_percent?: number;
  annualised_indirect_property_expenses_percent?: number;
  annualised_interest_on_borrowing_percent?: number;
  annualised_lending_and_borrowing_costs_percent?: number;
  annualised_one_off_costs_percent?: number;
  annualised_ongoing_charges_percent?: number;
  annualised_performance_fees_invoiced_percent?: number;
  annualised_performance_fees_paid_through_nav_percent?: number;
  annualised_performance_fees_percent?: number;
  annualised_property_expenses_percent?: number;
  annualised_revenue_paid_to_custodian_percent?: number;
  annualised_revenue_paid_to_lending_agent_percent?: number;
  annualised_revenue_paid_to_manager_percent?: number;
  annualised_stock_borrowing_fees_percent?: number;
  annualised_stock_lending_fees_not_retained_see_below_percent?: number;
  annualised_total_cost_percent?: number;
  annualised_transaction_costs_percent?: number;
  annualised_transaction_explicit_costs_percent?: number;
  annualised_transaction_taxes_percent?: number;
  borrowing_and_arrangement_fees?: number;
  borrowing_and_arrangement_fees_percent?: number;
  broker_commissions_exchange_fees?: number;
  broker_commissions_exchange_fees_percent?: number;
  carried_interest_charged?: number;
  carried_interest_charged_percent?: number;
  client_fx_costs?: number;
  client_fx_costs_percent?: number;
  direct_ongoing_charges?: number;
  direct_ongoing_charges_percent?: number;
  distribution_comms_and_client_service?: number;
  distribution_comms_and_client_service_percent?: number;
  entry_costs?: number;
  entry_costs_percent?: number;
  exit_costs?: number;
  exit_costs_percent?: number;
  fees_and_charges_paid_through_nav?: number;
  fees_and_charges_paid_through_nav_percent?: number;
  fund_and_investment_management?: number;
  fund_and_investment_management_percent?: number;
  governance_regulation_and_compliance?: number;
  governance_regulation_and_compliance_percent?: number;
  indirect_property_expenses?: number;
  indirect_property_expenses_percent?: number;
  interest_on_borrowing?: number;
  interest_on_borrowing_percent?: number;
  lending_and_borrowing_costs?: number;
  lending_and_borrowing_costs_percent?: number;
  one_off_costs?: number;
  one_off_costs_percent?: number;
  ongoing_charges?: number;
  ongoing_charges_percent?: number;
  performance_fees?: number;
  performance_fees_invoiced?: number;
  performance_fees_invoiced_percent?: number;
  performance_fees_paid_through_nav?: number;
  performance_fees_paid_through_nav_percent?: number;
  performance_fees_percent?: number;
  property_expenses?: number;
  property_expenses_percent?: number;
  revenue_paid_to_custodian?: number;
  revenue_paid_to_custodian_percent?: number;
  revenue_paid_to_lending_agent?: number;
  revenue_paid_to_lending_agent_percent?: number;
  revenue_paid_to_manager?: number;
  revenue_paid_to_manager_percent?: number;
  stock_borrowing_fees?: number;
  stock_borrowing_fees_percent?: number;
  stock_lending_fees_not_retained_see_below?: number;
  stock_lending_fees_not_retained_see_below_percent?: number;
  total_cost?: number;
  total_cost_percent?: number;
  transaction_costs?: number;
  transaction_costs_percent?: number;
  transaction_explicit_costs?: number;
  transaction_explicit_costs_percent?: number;
  transaction_taxes?: number;
  transaction_taxes_percent?: number;
  annualised_invoiced_fees_incl_vat_percent?: number;
  invoiced_fees_incl_vat?: number;
  invoiced_fees_incl_vat_percent?: number;
  annualised_audit_costs_percent?: number;
  annualised_custody_depositary_fees_percent?: number;
  annualised_denominated_administration_percent?: number;
  annualised_denominated_audit_costs_percent?: number;
  annualised_denominated_carried_interest_charged_percent?: number;
  annualised_denominated_custody_depositary_fees_percent?: number;
  annualised_denominated_direct_ongoing_charges_percent?: number;
  annualised_denominated_fees_and_charges_paid_through_nav_percent?: number;
  annualised_denominated_fund_and_investment_management_percent?: number;
  annualised_denominated_fund_set_up_costs_percent?: number;
  annualised_denominated_governance_regulation_and_compliance_percent?: number;
  annualised_denominated_interest_expense_percent?: number;
  annualised_denominated_legal_and_professional_fees_percent?: number;
  annualised_denominated_ongoing_charges_percent?: number;
  annualised_denominated_other_administration_charges_percent?: number;
  annualised_denominated_performance_fees_percent?: number;
  annualised_denominated_total_cost_percent?: number;
  annualised_denominated_transaction_costs_percent?: number;
  annualised_fund_set_up_costs_percent?: number;
  annualised_interest_expense_percent?: number;
  annualised_legal_and_professional_fees_percent?: number;
  annualised_other_administration_charges_percent?: number;
  audit_costs?: number;
  audit_costs_percent?: number;
  custody_depositary_fees?: number;
  custody_depositary_fees_percent?: number;
  denominated_administration_percent?: number;
  denominated_audit_costs_percent?: number;
  denominated_carried_interest_charged_percent?: number;
  denominated_custody_depositary_fees_percent?: number;
  denominated_direct_ongoing_charges_percent?: number;
  denominated_fees_and_charges_paid_through_nav_percent?: number;
  denominated_fund_and_investment_management_percent?: number;
  denominated_fund_set_up_costs_percent?: number;
  denominated_governance_regulation_and_compliance_percent?: number;
  denominated_interest_expense_percent?: number;
  denominated_legal_and_professional_fees_percent?: number;
  denominated_ongoing_charges_percent?: number;
  denominated_other_administration_charges_percent?: number;
  denominated_performance_fees_percent?: number;
  denominated_total_cost_percent?: number;
  denominated_transaction_costs_percent?: number;
  fund_set_up_costs?: number;
  fund_set_up_costs_percent?: number;
  interest_expense?: number;
  interest_expense_percent?: number;
  legal_and_professional_fees?: number;
  legal_and_professional_fees_percent?: number;
  other_administration_charges?: number;
  other_administration_charges_percent?: number;
  annualised_denominated_other_fees_percent?: number;
  annualised_denominated_transaction_fees_offset_percent?: number;
  annualised_other_fees_percent?: number;
  annualised_transaction_fees_offset_percent?: number;
  denominated_other_fees_percent?: number;
  denominated_transaction_fees_offset_percent?: number;
  other_fees?: number;
  other_fees_percent?: number;
  transaction_fees_offset?: number;
  transaction_fees_offset_percent?: number;
  annualised_broken_deal_fees_percent?: number;
  annualised_denominated_broken_deal_fees_percent?: number;
  annualised_denominated_facility_fees_eg_prime_brokerage_percent?: number;
  annualised_denominated_investment_administration_percent?: number;
  annualised_denominated_other_governance_and_compliance_charges_percent?: number;
  annualised_denominated_property_expenses_percent?: number;
  annualised_denominated_tax_advice_and_structuring_costs_percent?: number;
  annualised_facility_fees_eg_prime_brokerage_percent?: number;
  annualised_investment_administration_percent?: number;
  annualised_other_governance_and_compliance_charges_percent?: number;
  annualised_tax_advice_and_structuring_costs_percent?: number;
  broken_deal_fees?: number;
  broken_deal_fees_percent?: number;
  denominated_broken_deal_fees_percent?: number;
  denominated_facility_fees_eg_prime_brokerage_percent?: number;
  denominated_investment_administration_percent?: number;
  denominated_other_governance_and_compliance_charges_percent?: number;
  denominated_property_expenses_percent?: number;
  denominated_tax_advice_and_structuring_costs_percent?: number;
  facility_fees_eg_prime_brokerage?: number;
  facility_fees_eg_prime_brokerage_percent?: number;
  investment_administration?: number;
  investment_administration_percent?: number;
  other_governance_and_compliance_charges?: number;
  other_governance_and_compliance_charges_percent?: number;
  tax_advice_and_structuring_costs?: number;
  tax_advice_and_structuring_costs_percent?: number;
  annualised_collateral_management_fees_percent?: number;
  annualised_communication_material_percent?: number;
  annualised_distribution_costs_percent?: number;
  annualised_engagement_and_voting_fees_percent?: number;
  annualised_indirect_ongoing_charges_percent?: number;
  annualised_investment_advisers_fees_paid_through_NAV_percent?: number;
  annualised_less_management_fee_rebates_percent?: number;
  annualised_other_distribution_charges_percent?: number;
  annualised_other_explicit_charges_specify_percent?: number;
  annualised_payments_for_research_rpa_percent?: number;
  annualised_payments_for_research_rpa_through_nav_percent?: number;
  annualised_performance_measurement_percent?: number;
  annualised_risk_monitoring_percent?: number;
  annualised_transaction_anti_dilution_costs_percent?: number;
  annualised_transaction_implicit_costs_percent?: number;
  annualised_transaction_indirect_costs_percent?: number;
  annualised_transaction_related_services_percent?: number;
  collateral_management_fees?: number;
  collateral_management_fees_percent?: number;
  communication_material?: number;
  communication_material_percent?: number;
  distribution_costs?: number;
  distribution_costs_percent?: number;
  engagement_and_voting_fees?: number;
  engagement_and_voting_fees_percent?: number;
  indirect_ongoing_charges?: number;
  indirect_ongoing_charges_percent?: number;
  investment_advisers_fees_paid_through_NAV?: number;
  investment_advisers_fees_paid_through_NAV_percent?: number;
  less_management_fee_rebates?: number;
  less_management_fee_rebates_percent?: number;
  other_distribution_charges?: number;
  other_distribution_charges_percent?: number;
  other_explicit_charges_specify?: number;
  other_explicit_charges_specify_percent?: number;
  payments_for_research_rpa?: number;
  payments_for_research_rpa_percent?: number;
  payments_for_research_rpa_through_nav?: number;
  payments_for_research_rpa_through_nav_percent?: number;
  performance_measurement?: number;
  performance_measurement_percent?: number;
  risk_monitoring?: number;
  risk_monitoring_percent?: number;
  transaction_anti_dilution_costs?: number;
  transaction_anti_dilution_costs_percent?: number;
  transaction_implicit_costs?: number;
  transaction_implicit_costs_percent?: number;
  transaction_indirect_costs?: number;
  transaction_indirect_costs_percent?: number;
  transaction_related_services?: number;
  transaction_related_services_percent?: number;
  annualised_other_property_expenses_percent?: number;
  annualised_property_failed_transaction_costs_percent?: number;
  annualised_property_insurance_costs_percent?: number;
  annualised_property_leasing_costs_percent?: number;
  annualised_property_maintenance_and_repairs_percent?: number;
  annualised_property_management_fees_percent?: number;
  annualised_property_utilities_and_service_charges_percent?: number;
  annualised_property_valuation_fees_percent?: number;
  annualised_property_void_costs_percent?: number;
  other_property_expenses?: number;
  other_property_expenses_percent?: number;
  property_failed_transaction_costs?: number;
  property_failed_transaction_costs_percent?: number;
  property_insurance_costs?: number;
  property_insurance_costs_percent?: number;
  property_leasing_costs?: number;
  property_leasing_costs_percent?: number;
  property_maintenance_and_repairs?: number;
  property_maintenance_and_repairs_percent?: number;
  property_management_fees?: number;
  property_management_fees_percent?: number;
  property_utilities_and_service_charges?: number;
  property_utilities_and_service_charges_percent?: number;
  property_valuation_fees?: number;
  property_valuation_fees_percent?: number;
  property_void_costs?: number;
  property_void_costs_percent?: number;
  annualised_denominated_directors_fees_percent?: number;
  annualised_denominated_indirect_property_expenses_percent?: number;
  annualised_denominated_less_management_fee_rebates_percent?: number;
  annualised_denominated_monitoring_fees_percent?: number;
  annualised_denominated_other_property_expenses_percent?: number;
  annualised_denominated_property_failed_transaction_costs_percent?: number;
  annualised_denominated_property_insurance_costs_percent?: number;
  annualised_denominated_property_leasing_costs_percent?: number;
  annualised_denominated_property_maintenance_and_repairs_percent?: number;
  annualised_denominated_property_management_fees_percent?: number;
  annualised_denominated_property_utilities_and_service_charges_percent?: number;
  annualised_denominated_property_valuation_fees_percent?: number;
  annualised_denominated_property_void_costs_percent?: number;
  annualised_denominated_transaction_fees_percent?: number;
  annualised_denominated_underwriting_fees_percent?: number;
  annualised_directors_fees_percent?: number;
  annualised_monitoring_fees_percent?: number;
  annualised_transaction_fees_percent?: number;
  annualised_underwriting_fees_percent?: number;
  denominated_directors_fees_percent?: number;
  denominated_indirect_property_expenses_percent?: number;
  denominated_less_management_fee_rebates_percent?: number;
  denominated_monitoring_fees_percent?: number;
  denominated_other_property_expenses_percent?: number;
  denominated_property_failed_transaction_costs_percent?: number;
  denominated_property_insurance_costs_percent?: number;
  denominated_property_leasing_costs_percent?: number;
  denominated_property_maintenance_and_repairs_percent?: number;
  denominated_property_management_fees_percent?: number;
  denominated_property_utilities_and_service_charges_percent?: number;
  denominated_property_valuation_fees_percent?: number;
  denominated_property_void_costs_percent?: number;
  denominated_transaction_fees_percent?: number;
  denominated_underwriting_fees_percent?: number;
  directors_fees?: number;
  directors_fees_percent?: number;
  monitoring_fees?: number;
  monitoring_fees_percent?: number;
  transaction_fees?: number;
  transaction_fees_percent?: number;
  underwriting_fees?: number;
  underwriting_fees_percent?: number;
}

export interface AssetValuesV1 {
  // AuM
  has_valid_aum?: boolean;
  aumTag?: string;
  assets_under_management?: number;
  provided_assets_under_management?: number;
  factored_assets_under_management?: number;
  clientProvidedAum?: number;
  templateAum?: number;
  //
  // Alternate Asset Base Info
  //
  // Preferred Asset Base
  providedAssetBaseValue?: number;
  denominatedAum?: number;
  preferredAssetBase?: string;
  preferredAssetBaseSlug?: string;
  // Non Factored
  providedCommitmentValue?: number;
  providedDrawdownValue?: number;
  providedExposureValue?: number;
  // Client Provided
  clientProvidedCommitmentValue?: number;
  clientProvidedDrawdownValue?: number;
  clientProvidedExposureValue?: number;
  // Template Provided
  templateCommitmentValue?: number;
  templateDrawdownValue?: number;
  templateExposureValue?: number;
  //
}

export interface DateFields {
  // Factor Values
  annualization_factor?: number;
  de_annualization_factor?: number;
  // Factor Label
  factor_label?: string;
  factor_label_slug?: string;
  // Subscription and Redemption Dates
  subscription_date?: string;
  redemption_date?: string;
  provided_investment_period_range?: string;
  // Investment Period
  investment_period_start?: string;
  investment_period_end?: string;
  investment_period_range?: string;
  // Template Reporting Period
  template_reporting_period_start?: string;
  template_reporting_period_end?: string;
  template_reporting_period_range?: string;
}

export interface Comments {
  id?: string;
  body?: string;
  resource_type?: string;
  resource_id?: string;
  creator_type?: string;
  creator_id?: string;
  deleted_at?: Date;
  created_at?: Date;
  updated_at?: Date;
}

// Scheme Analysis
export interface SchemeAnalysis extends SchemeCostData {
  investmentId?: string;
  entityId?: string;
  parentId?: string;
  schemeName?: string;
  consultantId?: string;
  consultantName?: string;
  reportingPeriodStart?: string;
  reportingPeriodEnd?: string;
  reportingPeriodRange?: string;
  reportingPeriodId?: string;
  reportingPeriodYear?: number;
  reportingPeriodYearType?: string;
  rollup_tag?: string;
  assets_under_management?: number;
  managerCount?: number;
  completed?: number;
  requested?: number;
  completionRate?: number;
  sheetNumber?: number;
  reportNotes?: Notes[];
  serviceCostNotes?: Notes[];
  preferredCurrency?: string;
}

export interface SchemeCostData extends CostData {
  product_total_cost?: number;
  product_total_cost_percent?: number;
  annualised_product_total_cost_percent?: number;
  consultant_service_level_cost?: number;
  consultant_service_level_cost_percent?: number;
  annualised_consultant_service_level_cost_percent?: number;
}

export interface Notes {
  id: string;
  type: string;
  text: {
    value: string[];
  };
  entity_type: string;
  entity_id: string;
  organization_id: string;
  added_by: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
  added_by_type: string;
}

export interface SchemeAnalysisFilters {
  clientId: string;
  investmentIds?: string[];
  entityIds?: string[];
  timePeriodIds?: string[];
  consultantIds?: string[];
  schemeName?: string;
  page?: number;
  limit?: number;
  includeBenchmarking?: boolean;
  templateVersion?: string;
}

export interface SubmissionSummary {
  summary: SummaryAnalysis;
}

export interface SummaryAnalysis {
  investmentId?: string;
  entityId?: string;
  submissionId?: string;
  transCost: CostWithPercent;
  implicitTransCost: CostWithPercent;
  explicitTransCost: CostWithPercent;
  indirectTransCost: CostWithPercent;
  antiDilTransCost: CostWithPercent;
  oneOffCost: CostWithPercent;
  ongoingCharges: CostWithPercent;
  performanceFees: CostWithPercent;
  amcCost: CostWithPercent;
  afeCost: CostWithPercent;
  admin: CostWithPercent;
  grcCost: CostWithPercent;
  dccsCost: CostWithPercent;
  lendingCost: CostWithPercent;
  propertyCost: CostWithPercent;
  investmentManagementCost: CostWithPercent;
  directOngoingCharges: CostWithPercent;
  indirectOngoingCharges: CostWithPercent;
  ancillaryCost: CostWithPercent;
  otherFees: CostWithPercent;
  serviceCost: CostWithPercent;
  fiduciaryServiceCost: CostWithPercent;
  totalCost: CostWithPercent;
  totalProductCost: CostWithPercent;
  totalAum: CostWithPercent;
}

export interface CostWithPercent {
  amount?: number;
  formattedAmount: string;
  formattedCost: string;
  percent?: number | string;
  actualPercent?: number;
  annualPercent?: number;
  bpsValue?: number | string;
}

// Subfund Analysis
export interface SubfundAnalysis {
  investmentId?: string;
  entityId?: string;
  subfund_name?: string;
  fiduciary_name?: string;
  fiduciary_id?: string;
  reportingPeriodId?: string;
  reportingPeriodStart?: string;
  reportingPeriodEnd?: string;
  reportingPeriodRange?: string;
  rollup_tag?: string;
  assets_under_management?: number;
  completed?: number;
  requested?: number;
  completionRate?: number;
  childrenCount?: number;
  sheetNumber?: number;
  managerCount?: number;
  allManagerCount?: number;
  cost_data?: CostBreakdown;
  subfund_cost_notes?: any;
  root_investment_id?: string;
  locale: CustomLocale;
}

export interface CostBreakdown {
  product_cost?: CostDataWithAum;
  subfund_cost?: CostDataWithAum;
  total_cost?: CostDataWithAum;
}

export interface CostDataWithAum extends CostData {
  assets_under_management?: number;
}

export interface SubFundAnalysisFilters {
  clientId: string;
  investmentIds?: string[];
  entityIds?: string[];
  timePeriodIds?: string[];
  fiduciaryIds?: string[];
  subFundName?: string;
  page?: number;
  limit?: number;
}

export interface ConsultantAnalysisFilters {
  consultant_entity_id: string;
  time_period_id: string;
  currency_preference: CurrencyPreference;
}

export interface ProviderAnalysisFilters {
  provider_entity_id: string;
  time_period_id: string;
  currency_preference: CurrencyPreference;
}

export interface FiduciaryAnalysisFilters {
  entity_id: string;
  time_period_id: string;
  currency_preference: CurrencyPreference;
}

export interface OrganisationAnalysis {
  name: string;
  entityId: string;
  organisationTypeSlug: string;
  reportingPeriodId: string;
  reportingPeriodStart?: string;
  reportingPeriodEnd?: string;
  reportingPeriodRange?: string;
  assets_under_management?: number;
  cost_data?: CostBreakdown;
  sheetNumber?: number;
  currentLocale: CustomLocale;
}

export interface FiduciaryAnalysis {
  investmentId?: string;
  entityId?: string;
  fiduciary_id?: string;
  fiduciary_name?: string;
  reportingPeriodId?: string;
  reportingPeriodStart?: string;
  reportingPeriodEnd?: string;
  reportingPeriodRange?: string;
  rollup_tag?: string;
  assets_under_management?: number;
  cost_data?: CostBreakdown;
  sheetNumber?: number;
}
